import type { ReactNode } from 'react'

import { Icon, type IconName } from './Icon'

export const EMPTY_STATE_VARIANTS = [
  'no-data',
  'disconnected',
  'permission',
  'provider-lag',
  'no-results',
  'outage',
  'demo',
  'not-live',
] as const
export type EmptyStateVariant = (typeof EMPTY_STATE_VARIANTS)[number]

const COPY: Record<EmptyStateVariant, { icon: IconName; title: string; description: string }> = {
  'no-data': {
    icon: 'database',
    title: 'No data yet',
    description: 'Figures appear here once your website starts sending events, usually within an hour of connecting.',
  },
  disconnected: {
    icon: 'plug',
    title: 'Integration disconnected',
    description: 'Reconnect it to resume syncing. Anything after the disconnection is missing, not zero.',
  },
  permission: {
    icon: 'lock',
    title: 'You do not have access to this',
    description: 'Your role does not include this area. An Owner or Administrator can change that.',
  },
  'provider-lag': {
    icon: 'clock',
    title: 'Waiting on the provider',
    description: 'This provider reports with a delay, so the latest figures are incomplete. They fill in automatically.',
  },
  'no-results': {
    icon: 'search',
    title: 'Nothing matches these filters',
    description: 'Try a wider date range or clear a filter.',
  },
  outage: {
    icon: 'alert',
    title: 'Data is temporarily unavailable',
    description: 'We could not load this just now. Other figures on the page are unaffected.',
  },
  demo: {
    icon: 'sparkle',
    title: 'Demo data',
    description: 'You are looking at generated demo data. Connect your website to see your own figures.',
  },
  'not-live': {
    icon: 'plug',
    title: 'Not live yet',
    description: 'This section switches on once its data source is connected. The figures elsewhere are your own.',
  },
}

export interface EmptyStateProps {
  variant: EmptyStateVariant
  title?: string
  description?: ReactNode
  action?: ReactNode
  compact?: boolean
  /**
   * Announce this state to screen readers as it appears. Off by default: a
   * page of cards failing together should not fire an announcement per card.
   */
  announce?: boolean
}

/**
 * Why there is nothing to show. Each reason reads differently because each
 * asks the viewer to do something different — wait, reconnect, ask for access,
 * widen a filter — and "no data" must never be mistaken for "zero". `not-live`
 * is a section with no real data source yet (`useNotLive`): compact inside a
 * card, full size for a whole page.
 */
export function EmptyState({ variant, title, description, action, compact, announce }: EmptyStateProps) {
  const copy = COPY[variant]
  return (
    <div
      className="ci-empty"
      data-variant={variant}
      data-compact={compact ? 'true' : undefined}
      role={announce ? (variant === 'outage' ? 'alert' : 'status') : undefined}
    >
      <span className="ci-empty__icon">
        <Icon name={copy.icon} size={compact ? 18 : 22} />
      </span>
      <p className="ci-empty__title">{title ?? copy.title}</p>
      <p className="ci-empty__description">{description ?? copy.description}</p>
      {action ? <div className="ci-empty__action">{action}</div> : null}
    </div>
  )
}
